import { Link } from "react-router-dom";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const SCOPE = [
  { icon: "ri-tv-2-line", title: "伦理电视剧", desc: "以连续剧形式展开的伦理剧情作品，注重人物关系的长期铺陈。" },
  { icon: "ri-film-line", title: "伦理电影", desc: "以电影长片形式呈现的伦理题材，叙事凝练、影像讲究。" },
  { icon: "ri-history-line", title: "经典回顾", desc: "历经时间考验的伦理影视经典，是了解该类型的必看之作。" },
  { icon: "ri-movie-2-line", title: "最新上映", desc: "近期上映或上线的全新伦理剧情作品，紧跟创作趋势。" },
];

const GUIDE = [
  { step: "01", title: "浏览与检索", desc: "通过首页分类入口、顶部搜索或分类页筛选，快速定位感兴趣的作品。" },
  { step: "02", title: "查看详情", desc: "进入作品详情页，了解导演、主演、剧情简介与相关推荐。" },
  { step: "03", title: "收藏与分享", desc: "收藏作品保存在本机浏览器，分享为前端演示效果，便于后续回访。" },
  { step: "04", title: "反馈建议", desc: "通过下方表单提交你的建议，帮助我们持续完善内容与体验。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 日本伦理剧",
    "了解日本伦理剧网站的定位、内容范围与使用说明。我们专注于日本伦理剧情影视内容的展示与介绍，为影迷提供便捷的浏览与检索服务。",
    "日本伦理剧,关于我们,伦理剧情,日本影视,内容平台",
  );

  return (
    <div className="w-full">
      {/* 头部 */}
      <section className="w-full px-4 md:px-6 pt-10 pb-8">
        <nav aria-label="面包屑" className="text-sm font-label text-foreground-600 mb-4">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line mx-1"></i>
          <span className="text-foreground-900">关于</span>
        </nav>
        <h1 className="font-heading text-3xl font-bold text-foreground-950">关于日本伦理剧</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-700 max-w-3xl leading-relaxed">
          日本伦理剧是一个专注于日本伦理剧情影视内容的展示与介绍平台。我们面向热衷于日本伦理影视剧的影迷、研究者及文化爱好者，系统梳理从经典到新作的伦理电视剧与电影，提供清晰的作品信息与贴心的推荐浏览体验。
        </p>
      </section>

      {/* 内容范围 */}
      <section className="w-full px-4 md:px-6 pb-8">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-5">内容范围</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SCOPE.map((item) => (
            <div key={item.title} className="rounded-lg bg-background-50 border border-background-200/70 p-5">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
              <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 使用说明 */}
      <section className="w-full px-4 md:px-6 pb-8">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-5">使用说明</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {GUIDE.map((item) => (
            <div key={item.step} className="rounded-lg bg-primary-900 p-5">
              <span className="font-heading text-3xl font-bold text-accent-400">{item.step}</span>
              <h3 className="mt-3 font-heading text-base font-semibold text-background-50">{item.title}</h3>
              <p className="mt-1 text-sm text-background-200/85 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 联系方式 + 反馈表单 */}
      <section className="w-full px-4 md:px-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              如果你对内容有建议、发现信息有误，或希望与我们合作，欢迎通过以下方式联系。
            </p>
            <ul className="mt-5 flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 shrink-0">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="text-xs font-label text-foreground-500">电子邮箱（演示）</p>
                  <p className="text-sm text-foreground-900">contact@nihon-rinri-drama.example</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 shrink-0">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="text-xs font-label text-foreground-500">响应时间</p>
                  <p className="text-sm text-foreground-900">工作日 1-2 个工作日内回复</p>
                </div>
              </li>
            </ul>
            <p className="mt-6 text-xs font-label text-foreground-500 leading-relaxed">
              <i className="ri-information-line mr-1"></i>
              以上联系方式与响应说明均为演示内容。
            </p>
          </div>

          <div className="lg:col-span-3">
            <FeedbackForm />
          </div>
        </div>
      </section>

      {/* 版权声明 */}
      <section className="w-full px-4 md:px-6 pb-10">
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950">使用条款与版权声明</h2>
          <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-700 leading-relaxed">
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
              <span>本站为影视内容浏览与介绍站，所有文字与图片均为演示用途，<strong className="font-semibold">不提供真实播放、下载及会员服务</strong>。</span>
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
              <span>作品相关的名称、简介与分类信息用于内容展示，版权归各自权利人所有。</span>
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
              <span>收藏功能仅将数据保存在你的本地浏览器，不会上传至服务器。</span>
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
              <span>如有任何内容需要更正或下架，请通过上方联系方式与我们沟通。</span>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}